import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/services/api';
import Card from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import { Users as UsersIcon, Shield, UserCheck, UserX, Key, Clock, CheckCircle, XCircle, RefreshCw, Copy, Eye, EyeOff, Trash2, Edit, DollarSign } from 'lucide-react';
import { User, UserApproveRequest, ResetPasswordRequest, SalaryUpdateRequest } from '@/types';
import { generateUserPassword, copyToClipboard, checkPasswordStrength } from '@/utils/passwordGenerator';
import { Modal, InputNumber } from 'antd';

const Users: React.FC = () => {
  const { user } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [pendingUsers, setPendingUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'all' | 'pending'>('pending');
  const [resetPasswordForm, setResetPasswordForm] = useState<{
    userId: number | null;
    newPassword: string;
    showPassword: boolean;
    loading: boolean;
  }>({
    userId: null,
    newPassword: '',
    showPassword: false,
    loading: false,
  });
  const [message, setMessage] = useState<{
    type: 'success' | 'error' | null;
    text: string;
  }>({ type: null, text: '' });

  const [editUserModal, setEditUserModal] = useState<{
    visible: boolean;
    user: User | null;
    formData: {
      real_name: string;
      email: string;
      username: string;
      role: string;
      is_active: boolean;
    };
    loading: boolean;
  }>({
    visible: false,
    user: null,
    formData: {
      real_name: '',
      email: '',
      username: '',
      role: 'employee',
      is_active: true,
    },
    loading: false,
  });


  const [salaryEditForm, setSalaryEditForm] = useState<{
    userId: number | null;
    salaryType: 'monthly' | 'daily';
    amount: string;
    loading: boolean;
  }>({
    userId: null,
    salaryType: 'monthly',
    amount: '',
    loading: false,
  });

  const [approvalModal, setApprovalModal] = useState<{
    visible: boolean;
    userId: number | null;
    userName: string;
    selectedRole: 'employee' | 'contractor' | 'intern' | 'other';
    salary: number | null;
    salaryType: 'monthly' | 'daily';
    loading: boolean;
  }>({
    visible: false,
    userId: null,
    userName: '',
    selectedRole: 'employee',
    salary: null,
    salaryType: 'monthly',
    loading: false,
  });

  useEffect(() => {
    if (user?.role === 'admin') {
      fetchUsers();
      fetchPendingUsers();
    }
  }, [user]);

  const fetchUsers = async () => {
    try {
      const users = await api.getUsers();
      setUsers(users);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  };

  const fetchPendingUsers = async () => {
    try {
      const pendingUsers = await api.getPendingUsers();
      console.log('获取到的待审批用户:', pendingUsers);
      setPendingUsers(pendingUsers);
    } catch (error) {
      console.error('Failed to fetch pending users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApproveUser = async (userId: number, approveData: UserApproveRequest) => {
    try {
      console.log('审批用户:', userId, '数据:', approveData);
      await api.approveUser(userId, approveData);
      
      // 重新获取数据
      await Promise.all([fetchUsers(), fetchPendingUsers()]);
      
      // 显示成功消息
      const successMessage = approveData.status === 'approved' ? '用户审批通过!' : '用户已被拒绝';
      setMessage({ type: 'success', text: successMessage });
      setTimeout(() => setMessage({ type: null, text: '' }), 3000);
      
      // 如果是批准，自动切换到所有用户标签页
      if (approveData.status === 'approved') {
        setActiveTab('all');
      }
    } catch (error: any) {
      console.error('Failed to approve user:', error);
      setMessage({ 
        type: 'error', 
        text: `审批失败: ${error.response?.data?.detail || error.message}` 
      });
      setTimeout(() => setMessage({ type: null, text: '' }), 5000);
    }
  };

  const handleResetPassword = async (userId: number) => {
    if (!resetPasswordForm.newPassword) return;
    
    // 验证密码强度
    const passwordStrength = checkPasswordStrength(resetPasswordForm.newPassword);
    if (passwordStrength.level === 'weak') {
      setMessage({ type: 'error', text: '密码强度太弱，请使用更强的密码' });
      setTimeout(() => setMessage({ type: null, text: '' }), 3000);
      return;
    }
    
    if (passwordStrength.level === 'fair') {
      setMessage({ type: 'error', text: '密码强度一般，建议使用更强的密码' });
      setTimeout(() => setMessage({ type: null, text: '' }), 3000);
      return;
    }
    
    setResetPasswordForm(prev => ({ ...prev, loading: true }));
    
    try {
      const resetData: ResetPasswordRequest = {
        new_password: resetPasswordForm.newPassword,
      };
      await api.resetUserPassword(userId, resetData);
      setResetPasswordForm({ userId: null, newPassword: '', showPassword: false, loading: false });
      setMessage({ type: 'success', text: '密码重置成功！' });
      setTimeout(() => setMessage({ type: null, text: '' }), 3000);
    } catch (error: any) {
      console.error('Failed to reset password:', error);
      setMessage({ 
        type: 'error', 
        text: `密码重置失败: ${error.response?.data?.detail || error.message}` 
      });
      setTimeout(() => setMessage({ type: null, text: '' }), 5000);
      setResetPasswordForm(prev => ({ ...prev, loading: false }));
    }
  };

  const handleDeleteUser = async (userId: number, username: string) => {
    if (!window.confirm(`确定要删除用户 "${username}" 吗？此操作不可撤销。`)) {
      return;
    }
    
    try {
      await api.deleteUser(userId);
      await Promise.all([fetchUsers(), fetchPendingUsers()]);
      alert('用户删除成功！');
    } catch (error: any) {
      console.error('Failed to delete user:', error);
      const errorMessage = error.response?.data?.detail || error.message;
      alert(errorMessage);
    }
  };

  const handleUpdateSalary = async (userId: number) => {
    if (!salaryEditForm.amount) return;
    
    setSalaryEditForm(prev => ({ ...prev, loading: true }));
    
    try {
      const salaryData: SalaryUpdateRequest = {
        salary_type: salaryEditForm.salaryType,
        amount: Number(salaryEditForm.amount),
      };
      await api.updateUserSalary(userId, salaryData);
      setSalaryEditForm({ userId: null, salaryType: 'monthly', amount: '', loading: false });
      await Promise.all([fetchUsers(), fetchPendingUsers()]);
      setMessage({ type: 'success', text: '工资更新成功！' });
      setTimeout(() => setMessage({ type: null, text: '' }), 3000);
    } catch (error: any) {
      console.error('Failed to update salary:', error);
      setMessage({ 
        type: 'error', 
        text: `工资更新失败: ${error.response?.data?.detail || error.message}` 
      });
      setTimeout(() => setMessage({ type: null, text: '' }), 5000);
      setSalaryEditForm(prev => ({ ...prev, loading: false }));
    }
  };

  const generateAndSetPassword = () => {
    const newPassword = generateUserPassword();
    setResetPasswordForm(prev => ({ ...prev, newPassword }));
  };

  const copyPasswordToClipboard = async () => {
    if (resetPasswordForm.newPassword) {
      const success = await copyToClipboard(resetPasswordForm.newPassword);
      if (success) {
        setMessage({ type: 'success', text: '密码已复制到剪贴板！' });
        setTimeout(() => setMessage({ type: null, text: '' }), 2000);
      } else {
        setMessage({ type: 'error', text: '复制失败，请手动复制' });
        setTimeout(() => setMessage({ type: null, text: '' }), 3000);
      }
    }
  };

  const closeApprovalModal = () => {
    setApprovalModal({
      visible: false,
      userId: null,
      userName: '',
      selectedRole: 'employee',
      salary: null,
      salaryType: 'monthly',
      loading: false,
    });
  };

  const openEditUserModal = (userData: User) => {
    setEditUserModal({
      visible: true,
      user: userData,
      formData: {
        real_name: userData.real_name,
        email: userData.email,
        username: userData.username,
        role: userData.role,
        is_active: userData.is_active,
      },
      loading: false,
    });
  };

  const closeEditUserModal = () => {
    setEditUserModal({
      visible: false,
      user: null,
      formData: {
        real_name: '',
        email: '',
        username: '',
        role: 'employee',
        is_active: true,
      },
      loading: false,
    });
  };

  const handleUpdateUser = async () => {
    if (!editUserModal.user) return;
    
    setEditUserModal(prev => ({ ...prev, loading: true }));
    
    try {
      // Only send changed fields
      const updateData: Partial<User> = {};
      const original = editUserModal.user;
      const current = editUserModal.formData;
      
      if (current.real_name !== original.real_name) updateData.real_name = current.real_name;
      if (current.email !== original.email) updateData.email = current.email;
      if (current.username !== original.username) updateData.username = current.username;
      if (current.role !== original.role) updateData.role = current.role as any;
      if (current.is_active !== original.is_active) updateData.is_active = current.is_active;
      
      if (Object.keys(updateData).length === 0) {
        setMessage({ type: 'error', text: '没有检测到任何更改' });
        setTimeout(() => setMessage({ type: null, text: '' }), 3000);
        setEditUserModal(prev => ({ ...prev, loading: false }));
        return;
      }
      
      await api.updateUser(editUserModal.user.id, updateData);
      await Promise.all([fetchUsers(), fetchPendingUsers()]);
      closeEditUserModal();
      setMessage({ type: 'success', text: '用户信息更新成功！' });
      setTimeout(() => setMessage({ type: null, text: '' }), 3000);
    } catch (error: any) {
      console.error('Failed to update user:', error);
      setMessage({ 
        type: 'error', 
        text: `更新失败: ${error.response?.data?.detail || error.message}` 
      });
      setTimeout(() => setMessage({ type: null, text: '' }), 5000);
      setEditUserModal(prev => ({ ...prev, loading: false }));
    }
  };

  const getRoleLabel = (role: string) => {
    const labels = {
      admin: '管理员',
      employee: '正式用户',
      contractor: '外包',
      intern: '实习',
      other: '其他',
    };
    return labels[role as keyof typeof labels] || role;
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
          <Clock className="w-3 h-3 mr-1" />
          待审批
        </span>;
      case 'approved':
        return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
          <CheckCircle className="w-3 h-3 mr-1" />
          已批准
        </span>;
      case 'rejected':
        return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
          <XCircle className="w-3 h-3 mr-1" />
          已拒绝
        </span>;
      default:
        return status;
    }
  };

  if (user?.role !== 'admin') {
    return (
      <div className="space-y-6">
        <h1 className="text-3xl font-bold text-gray-900">用户管理</h1>
        
        <Card>
          <Card.Content className="p-6 text-center">
            <Shield className="h-12 w-12 mx-auto mb-4 text-red-300" />
            <p className="text-lg font-medium mb-2">访问受限</p>
            <p className="text-sm text-gray-500">只有管理员可以访问用户管理页面</p>
          </Card.Content>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="space-y-6">
        <h1 className="text-3xl font-bold text-gray-900">用户管理</h1>
        <Card>
          <Card.Content className="p-6 text-center">
            <p>加载中...</p>
          </Card.Content>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* 消息提示（除删除用户外的其他功能使用） */}
      {message.type && (
        <div className={`p-4 rounded-md ${
          message.type === 'success' 
            ? 'bg-green-50 text-green-800 border border-green-200' 
            : 'bg-red-50 text-red-800 border border-red-200'
        }`}>
          <div className="flex items-center">
            {message.type === 'success' ? (
              <CheckCircle className="h-4 w-4 mr-2" />
            ) : (
              <XCircle className="h-4 w-4 mr-2" />
            )}
            {message.text}
          </div>
        </div>
      )}

      
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold text-gray-900">用户管理</h1>
        <div className="flex space-x-2">
          <Button
            variant={activeTab === 'pending' ? 'primary' : 'secondary'}
            onClick={() => setActiveTab('pending')}
          >
            待审批用户 ({pendingUsers.length})
          </Button>
          <Button
            variant={activeTab === 'all' ? 'primary' : 'secondary'}
            onClick={() => setActiveTab('all')}
          >
            所有用户 ({users.length})
          </Button>
        </div>
      </div>

      {activeTab === 'pending' && (
        <Card>
          <Card.Header className="flex justify-between items-center">
            <Card.Title>待审批用户</Card.Title>
            <Button 
              size="sm" 
              variant="secondary"
              onClick={() => {
                setLoading(true);
                fetchPendingUsers();
              }}
            >
              刷新
            </Button>
          </Card.Header>
          <Card.Content>
            {pendingUsers.length === 0 ? (
              <p className="text-gray-500 text-center py-8">暂无待审批用户</p>
            ) : (
              <div className="space-y-4">
                {pendingUsers.map((pendingUser) => (
                  <div key={pendingUser.id} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <h3 className="font-semibold text-lg">{pendingUser.real_name || pendingUser.username}</h3>
                          {getStatusBadge(pendingUser.status)}
                        </div>
                        <p className="text-gray-600 mb-1">邮箱: {pendingUser.email}</p>
                        <p className="text-gray-500 text-sm">
                          注册时间: {new Date(pendingUser.created_at).toLocaleString()}
                        </p>
                      </div>
                      <div className="flex space-x-2">
                        <Button
                          size="sm"
                          variant="primary"
                          onClick={() => {
                            setApprovalModal({
                              visible: true,
                              userId: pendingUser.id,
                              userName: pendingUser.real_name || pendingUser.username,
                              selectedRole: 'employee',
                              salary: null,
                              salaryType: 'monthly',
                              loading: false,
                            });
                          }}
                        >
                          <UserCheck className="w-4 h-4 mr-1" />
                          批准
                        </Button>
                        <Button
                          size="sm"
                          variant="danger"
                          onClick={() => {
                            if (confirm(`确定要拒绝用户 "${pendingUser.real_name || pendingUser.username}" 的注册申请吗？`)) {
                              handleApproveUser(pendingUser.id, { status: 'rejected' as const });
                            }
                          }}
                        >
                          <UserX className="w-4 h-4 mr-1" />
                          拒绝
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card.Content>
        </Card>
      )}

      {activeTab === 'all' && (
        <Card>
          <Card.Header>
            <Card.Title>所有用户</Card.Title>
          </Card.Header>
          <Card.Content>
            <div className="space-y-4">
              {users.map((userData) => (
                <div key={userData.id} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <h3 className="font-semibold text-lg">{userData.real_name || userData.username}</h3>
                        {getStatusBadge(userData.status)}
                        <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs">
                          {getRoleLabel(userData.role)}
                        </span>
                      </div>
                      <p className="text-gray-600 mb-1">邮箱: {userData.email}</p>
                      {(userData.monthly_salary || userData.daily_salary) && (
                        <p className="text-gray-600 mb-1">
                          月工资: ¥{userData.monthly_salary} (日工资: ¥{userData.daily_salary})
                        </p>
                      )}
                      <p className="text-gray-500 text-sm">
                        创建时间: {new Date(userData.created_at).toLocaleString()}
                      </p>
                    </div>
                    <div className="flex flex-col space-y-2">
                      {/* 工资编辑表单 */}
                      {salaryEditForm.userId === userData.id ? (
                        <div className="flex flex-col space-y-2 border border-gray-200 rounded p-3 bg-gray-50">
                          <div className="flex items-center space-x-2">
                            <select
                              value={salaryEditForm.salaryType}
                              onChange={(e) => setSalaryEditForm(prev => ({
                                ...prev,
                                salaryType: e.target.value as 'monthly' | 'daily'
                              }))}
                              className="px-3 py-1 border border-gray-300 rounded text-sm"
                            >
                              <option value="monthly">月工资</option>
                              <option value="daily">日工资</option>
                            </select>
                            <Input
                              type="number"
                              placeholder="金额"
                              value={salaryEditForm.amount}
                              onChange={(e) => setSalaryEditForm(prev => ({
                                ...prev,
                                amount: e.target.value
                              }))}
                              className="w-32"
                            />
                          </div>
                          <div className="flex items-center space-x-2">
                            <Button
                              size="sm"
                              onClick={() => handleUpdateSalary(userData.id)}
                              disabled={!salaryEditForm.amount || salaryEditForm.loading}
                              loading={salaryEditForm.loading}
                            >
                              确认更新
                            </Button>
                            <Button
                              size="sm"
                              variant="secondary"
                              onClick={() => setSalaryEditForm({ 
                                userId: null, 
                                salaryType: 'monthly', 
                                amount: '', 
                                loading: false 
                              })}
                              disabled={salaryEditForm.loading}
                            >
                              取消
                            </Button>
                          </div>
                        </div>
                      ) : null}
                      
                      {/* 密码重置表单 */}
                      <div className="flex space-x-2">
                      {resetPasswordForm.userId === userData.id ? (
                        <div className="flex flex-col space-y-2">
                          {/* 密码输入区域 */}
                          <div className="flex items-center space-x-2">
                            <div className="relative">
                              <Input
                                type={resetPasswordForm.showPassword ? "text" : "password"}
                                placeholder="新密码"
                                value={resetPasswordForm.newPassword}
                                onChange={(e) => setResetPasswordForm(prev => ({
                                  ...prev,
                                  newPassword: e.target.value,
                                }))}
                                className="w-64 pr-20"
                              />
                              <div className="absolute right-2 top-1/2 transform -translate-y-1/2 flex space-x-1">
                                <Button
                                  size="sm"
                                  variant="secondary"
                                  onClick={() => setResetPasswordForm(prev => ({
                                    ...prev,
                                    showPassword: !prev.showPassword
                                  }))}
                                  className="p-1 h-6 w-6"
                                >
                                  {resetPasswordForm.showPassword ? 
                                    <EyeOff className="h-3 w-3" /> : 
                                    <Eye className="h-3 w-3" />
                                  }
                                </Button>
                                <Button
                                  size="sm"
                                  variant="secondary"
                                  onClick={copyPasswordToClipboard}
                                  disabled={!resetPasswordForm.newPassword}
                                  className="p-1 h-6 w-6"
                                  title="复制密码"
                                >
                                  <Copy className="h-3 w-3" />
                                </Button>
                              </div>
                            </div>
                          </div>
                          
                          {/* 密码强度指示器 */}
                          {resetPasswordForm.newPassword && (
                            <div className="w-64">
                              {(() => {
                                const strength = checkPasswordStrength(resetPasswordForm.newPassword);
                                const colors = {
                                  weak: 'bg-red-200',
                                  fair: 'bg-yellow-200', 
                                  good: 'bg-blue-200',
                                  strong: 'bg-green-200'
                                };
                                return (
                                  <div className="space-y-1">
                                    <div className="flex justify-between text-xs">
                                      <span>密码强度: {strength.level}</span>
                                      <span>{strength.score}/100</span>
                                    </div>
                                    <div className="w-full bg-gray-200 rounded-full h-1">
                                      <div 
                                        className={`h-1 rounded-full ${colors[strength.level]}`}
                                        style={{ width: `${strength.score}%` }}
                                      ></div>
                                    </div>
                                  </div>
                                );
                              })()}
                            </div>
                          )}
                          
                          {/* 操作按钮 */}
                          <div className="flex items-center space-x-2">
                            <Button
                              size="sm"
                              variant="secondary"
                              onClick={generateAndSetPassword}
                              className="flex items-center"
                            >
                              <RefreshCw className="h-3 w-3 mr-1" />
                              生成强密码
                            </Button>
                            <Button
                              size="sm"
                              onClick={() => handleResetPassword(userData.id)}
                              disabled={!resetPasswordForm.newPassword || resetPasswordForm.loading}
                              loading={resetPasswordForm.loading}
                            >
                              确认重置
                            </Button>
                            <Button
                              size="sm"
                              variant="secondary"
                              onClick={() => setResetPasswordForm({ 
                                userId: null, 
                                newPassword: '', 
                                showPassword: false, 
                                loading: false 
                              })}
                              disabled={resetPasswordForm.loading}
                            >
                              取消
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <Button
                            size="sm"
                            variant="secondary"
                            onClick={() => openEditUserModal(userData)}
                          >
                            <Edit className="w-4 h-4 mr-1" />
                            编辑信息
                          </Button>

                          <Button
                            size="sm"
                            variant="secondary"
                            onClick={() => {
                              const newPassword = generateUserPassword();
                              setResetPasswordForm({ 
                                userId: userData.id, 
                                newPassword,
                                showPassword: true,
                                loading: false
                              });
                              // 自动复制到剪贴板
                              copyToClipboard(newPassword).then(success => {
                                if (success) {
                                  setMessage({ type: 'success', text: '已生成强密码并复制到剪贴板！' });
                                  setTimeout(() => setMessage({ type: null, text: '' }), 2000);
                                }
                              });
                            }}
                          >
                            <Key className="w-4 h-4 mr-1" />
                            重置密码
                          </Button>
                          
                          <Button
                            size="sm"
                            variant="secondary"
                            onClick={() => {
                              const currentSalary = userData.salary_type === 'monthly' 
                                ? userData.monthly_salary 
                                : userData.daily_salary;
                              setSalaryEditForm({
                                userId: userData.id,
                                salaryType: (userData.salary_type as 'monthly' | 'daily') || 'monthly',
                                amount: currentSalary ? currentSalary.toString() : '',
                                loading: false
                              });
                            }}
                          >
                            <DollarSign className="w-4 h-4 mr-1" />
                            编辑工资
                          </Button>
                          
                          <Button
                            size="sm"
                            variant="danger"
                            onClick={() => handleDeleteUser(userData.id, userData.real_name || userData.username)}
                          >
                            <Trash2 className="w-4 h-4 mr-1" />
                            删除用户
                          </Button>
                        </>
                      )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </Card.Content>
        </Card>
      )}

      {/* 角色选择和工资设置Modal */}
      <Modal
        title="用户审批"
        open={approvalModal.visible}
        onCancel={closeApprovalModal}
        footer={[
          <Button
            key="cancel"
            variant="secondary"
            onClick={closeApprovalModal}
            disabled={approvalModal.loading}
          >
            取消
          </Button>,
          <Button
            key="approve"
            variant="primary"
            loading={approvalModal.loading}
            onClick={async () => {
              setApprovalModal(prev => ({ ...prev, loading: true }));
              try {
                await handleApproveUser(approvalModal.userId!, {
                  status: 'approved' as const,
                  role: approvalModal.selectedRole,
                  salary: approvalModal.salary || undefined,
                  salary_type: approvalModal.salaryType,
                });
                closeApprovalModal();
              } catch (error) {
                setApprovalModal(prev => ({ ...prev, loading: false }));
              }
            }}
          >
            确认批准
          </Button>
        ]}
      >
        <div className="space-y-4">
          <div>
            <p className="mb-2">用户: <strong>{approvalModal.userName}</strong></p>
          </div>
          
          <div>
            <p className="mb-3 font-medium">选择角色:</p>
            <div>
              <label>
                <input
                  type="radio"
                  name="role"
                  value="employee"
                  checked={approvalModal.selectedRole === 'employee'}
                  onChange={(e) => setApprovalModal(prev => ({ ...prev, selectedRole: e.target.value as any }))}
                />
                正式用户
              </label>
              <br />
              <label>
                <input
                  type="radio"
                  name="role"
                  value="contractor"
                  checked={approvalModal.selectedRole === 'contractor'}
                  onChange={(e) => setApprovalModal(prev => ({ ...prev, selectedRole: e.target.value as any }))}
                />
                外包
              </label>
              <br />
              <label>
                <input
                  type="radio"
                  name="role"
                  value="intern"
                  checked={approvalModal.selectedRole === 'intern'}
                  onChange={(e) => setApprovalModal(prev => ({ ...prev, selectedRole: e.target.value as any }))}
                />
                实习
              </label>
              <br />
              <label>
                <input
                  type="radio"
                  name="role"
                  value="other"
                  checked={approvalModal.selectedRole === 'other'}
                  onChange={(e) => setApprovalModal(prev => ({ ...prev, selectedRole: e.target.value as any }))}
                />
                其他
              </label>
            </div>
          </div>

          <div>
            <p className="mb-3 font-medium">设置工资 (外包人员可留空):</p>
            
            <div className="mb-3">
              <label>
                <input
                  type="radio"
                  name="salaryType"
                  value="monthly"
                  checked={approvalModal.salaryType === 'monthly'}
                  onChange={(e) => setApprovalModal(prev => ({ ...prev, salaryType: e.target.value as 'monthly' | 'daily' }))}
                />
                月工资
              </label>
              &nbsp;&nbsp;
              <label>
                <input
                  type="radio"
                  name="salaryType"
                  value="daily"
                  checked={approvalModal.salaryType === 'daily'}
                  onChange={(e) => setApprovalModal(prev => ({ ...prev, salaryType: e.target.value as 'monthly' | 'daily' }))}
                />
                日工资
              </label>
            </div>

            <InputNumber
              style={{ width: '100%' }}
              placeholder={approvalModal.salaryType === 'monthly' ? '请输入月工资' : '请输入日工资'}
              min={0}
              precision={0}
              value={approvalModal.salary}
              onChange={(value) => setApprovalModal(prev => ({ ...prev, salary: value }))}
              formatter={(value) => `¥ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              parser={(value) => value?.replace(/¥\s?|(,*)/g, '') as any}
            />
          </div>
        </div>
      </Modal>

      {/* 编辑用户信息Modal */}
      <Modal
        title="编辑用户信息"
        open={editUserModal.visible}
        onCancel={closeEditUserModal}
        width={600}
        footer={[
          <Button
            key="cancel"
            variant="secondary"
            onClick={closeEditUserModal}
            disabled={editUserModal.loading}
          >
            取消
          </Button>,
          <Button
            key="save"
            variant="primary"
            loading={editUserModal.loading}
            onClick={handleUpdateUser}
          >
            保存更改
          </Button>
        ]}
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">真实姓名</label>
            <Input
              value={editUserModal.formData.real_name}
              onChange={(e) => setEditUserModal(prev => ({
                ...prev,
                formData: { ...prev.formData, real_name: e.target.value }
              }))}
              placeholder="请输入真实姓名"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">用户名</label>
            <Input
              value={editUserModal.formData.username}
              onChange={(e) => setEditUserModal(prev => ({
                ...prev,
                formData: { ...prev.formData, username: e.target.value }
              }))}
              placeholder="请输入用户名"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">邮箱地址</label>
            <Input
              type="email"
              value={editUserModal.formData.email}
              onChange={(e) => setEditUserModal(prev => ({
                ...prev,
                formData: { ...prev.formData, email: e.target.value }
              }))}
              placeholder="请输入邮箱地址"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">用户角色</label>
            <select
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={editUserModal.formData.role}
              onChange={(e) => setEditUserModal(prev => ({
                ...prev,
                formData: { ...prev.formData, role: e.target.value }
              }))}
            >
              <option value="employee">正式用户</option>
              <option value="contractor">外包</option>
              <option value="intern">实习</option>
              <option value="other">其他</option>
              <option value="supervisor">督导</option>
              {editUserModal.user?.role === 'admin' && (
                <option value="admin">管理员</option>
              )}
            </select>
          </div>

          <div>
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={editUserModal.formData.is_active}
                onChange={(e) => setEditUserModal(prev => ({
                  ...prev,
                  formData: { ...prev.formData, is_active: e.target.checked }
                }))}
                className="rounded border-gray-300 focus:ring-blue-500"
              />
              <span className="text-sm font-medium">账户激活状态</span>
            </label>
            <p className="text-xs text-gray-500 mt-1">
              取消激活后，用户将无法登录系统
            </p>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default Users;